<template>
    <section class="wrap wrap-large py-12">
        <form
            class="pb-8 at1024:pb-16 px-8 at1024:px-16 border w-full mx-auto border-slate-300 at1024:my-8"
            @submit="submit"
            :class="{ errors: errors }"
            v-if="registrationEnabled"
        >
            <header class="text-center pt-[2em]">
                <h1
                    class="text-[40px] at720:text-4xl my-[30px] at1024:text-[80px]"
                >
                    {{ $t("Registration") }}
                </h1>
            </header>

            <div class="text-center">
                <p
                    class="font-medium text-[28px] at1024:text-[35px] mb-[1.1em]"
                >
                    {{ $t("Account Setup") }}
                </p>
            </div>

            <fieldset class="flex flex-col gap-3">
                <div class="at768:flex-row flex-col flex gap-3">
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="text"
                            label="First Name"
                            :animated="true"
                            :contrast="true"
                            v-model="form.first_name"
                            maxlength="30"
                            required
                        />
                    </div>
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="text"
                            label="Last Name"
                            :animated="true"
                            :contrast="true"
                            v-model="form.last_name"
                            maxlength="30"
                            required
                        />
                    </div>
                </div>
                <SeclockInput
                    type="text"
                    label="Job Title"
                    v-model="form.title"
                    :animated="true"
                    :contrast="true"
                    maxlength="50"
                    required
                />
                <SeclockInput
                    type="email"
                    label="Email"
                    v-model="form.email"
                    :animated="true"
                    :contrast="true"
                    maxlength="200"
                    required
                />
                <SeclockInput
                    type="email"
                    label="Email for Invoices"
                    v-model="form.email_for_invoices"
                    :animated="true"
                    :contrast="true"
                    maxlength="200"
                />
                <PasswordRequirements :password="form.password" />
                <div class="at768:flex-row flex-col flex gap-3">
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="password"
                            label="Password"
                            v-model="form.password"
                            :error="form.errors?.password"
                            :class="{invalid: form.errors?.password?.length}"
                            :animated="true"
                            :contrast="true"
                            @invalid="invalidate"
                            required
                            pattern="^\S{8,20}$"
                            describedby="password-requirements"
                        />
                    </div>
                    <div class="at768:w-1/2">
                        <SeclockInput
                            type="password"
                            label="Confirm Password"
                            v-model="form.password_confirmation"
                            :class="{invalid: form.errors?.password?.length}"
                            :animated="true"
                            :contrast="true"
                            pattern="^\S{8,36}$"
                            required
                        />
                    </div>
                </div>
            </fieldset>

            <div class="flex justify-between items-center mt-[1em] mb-[2em]">
                <button
                    @click="goBack"
                    type="button"
                    class="uppercase tracking-widest flex gap-1 items-center"
                >
                    <BaseSvg name="icon-chevron-left" />
                    {{ $t('PREV') }}
                </button>
                <button type="submit" :disabled="!isFormValid" class="btn-white-on-black">
                    {{ $t("Next") }}
                    <BaseSvg name="icon-chevron-right" />
                </button>
            </div>
            <RecaptchaDisclaimer class="mt-16 text-center text-gray text-sm" />
        </form>
        <div 
            v-else
            class="flex flex-col items-center justify-center gap-4 mt-40 mb-40 max-w-[1200px] mx-auto"
        >
            <ServiceUnavailableNotice
                title="Service unavailable"
                description="Online registration is down for maintenance and currently unavailable. We estimate that this service will be back online by Tuesday 3/10."
                contact-info="Please contact us at 800-847-5625 to register."
            />
        </div>
    </section>
</template>

<script setup>
import { BaseSvg, RecaptchaDisclaimer, SeclockInput, PasswordRequirements, ServiceUnavailableNotice } from "@/Components";
import { eventBus } from "@/Composables";
import { computed } from "vue";

const props = defineProps(["form", "errors", "registrationEnabled"]);

const invalidate = () => {
    emit("invalidate");
};

const isFormValid = computed(() => {
    // Validate first and last names
    if (!props.form.first_name.trim() || !props.form.last_name.trim()) return false;

    // Job Title validation
    if (!props.form.title.trim()) return false;

    // Email validation using a basic email pattern
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(props.form.email)) return false;

    // Email for Invoices validation (optional unless required by business logic)
    if (props.form.email_for_invoices && !emailPattern.test(props.form.email_for_invoices))
        return false;

    //SEC-2645 : Remove this validation and allow the form to generate an error toast
    // Password validation (make sure it matches the required pattern and is confirmed correctly)
    // const passwordPattern = /[a-zA-Z0-9?!.*\s]{8,36}/;
    // if (
    //     !passwordPattern.test(props.form.password) ||
    //     !passwordPattern.test(props.form.password_confirmation) ||
    //     props.form.password !== props.form.password_confirmation
    // ) {
    //     return false;
    // }

    // All validations pass
    return true;
});

const goBack = () => {
    emit("goToNextStep", {
        step: "RegistrationStep1",
    });
};

const submit = (event) => {
    event.preventDefault();

    if (passwords_match.value) {
        emit("goToNextStep", {
            step: "RegistrationStep3",
        });
    } else {
        eventBus.emit("alert:show", {
            title: "Error",
            message:
                "Password fields must match and be 8 or more characters, one uppercase, one lowercase, and one number.",
            level: "danger",
        });
    }
};

const passwords_match = computed(
    () => props.form.password === props.form.password_confirmation
);

const emit = defineEmits(["goToNextStep", "invalidate"]);
</script>
